// @ts-nocheck

const MAX_RECEIPT_BYTES = 20971520;

const RECEIPT_PREREQUISITE_MESSAGE =
  "Please complete all required fields above before uploading your receipt.";

const REGISTRATION_SUCCESS_MESSAGE =
  "Thank you for registering your mattress with SleepMaker New Zealand.";

/**
 * Product registration form: DataJet submit, receipt size checks, Akismet timestamp.
 */
class ProductRegistration extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {Record<string, unknown[]> | null} */
  #regions = null;

  /** @returns {Record<string, unknown[]>} */
  #getRegions() {
    if (this.#regions) return this.#regions;

    const script = this.querySelector(
      "script[data-product-registration-regions]",
    );
    const raw = script?.textContent?.trim();
    if (raw && raw !== "null") {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          this.#regions = parsed;
          return parsed;
        }
      } catch {
        /* fall through to empty fallback */
      }
    }

    this.#regions = {};
    return this.#regions;
  }

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const form =
      this.querySelector("form#gform_23") ||
      this.querySelector("form[data-datajet-token]");
    if (!form) return;

    this.#setupChainedSelects(signal);
    this.#stampAkismetField();
    this.#bindReceiptValidation(form, signal);
    this.#bindSubmit(form, signal);
  }

  /**
   * Region ← region keys; Retailer ← array for region; Store ← retailer.choices;
   * Range ← store.choices.
   * @param {AbortSignal} signal
   */
  #setupChainedSelects(signal) {
    const regions = this.#getRegions();
    const regionSel = this.querySelector('select[name="region"]');
    const retailSel = this.querySelector('select[name="retail"]');
    const storeSel = this.querySelector('select[name="store"]');
    const rangeSel = this.querySelector('select[name="range"]');
    if (!regionSel || !retailSel || !storeSel || !rangeSel) return;

    this.#populateRegionOptions(regionSel, regions);
    this.#resetSelectPlaceholderOnly(retailSel);
    this.#resetSelectPlaceholderOnly(storeSel);
    this.#resetSelectPlaceholderOnly(rangeSel);

    const onRegionChange = () => {
      const regionKey = regionSel.value;
      this.#resetSelectPlaceholderOnly(retailSel);
      this.#resetSelectPlaceholderOnly(storeSel);
      this.#resetSelectPlaceholderOnly(rangeSel);
      if (!regionKey || !regions[regionKey]) return;
      const retailers = regions[regionKey];
      this.#fillSelectFromNodes(retailSel, retailers);
    };

    const onRetailChange = () => {
      const regionKey = regionSel.value;
      const retailVal = retailSel.value;
      this.#resetSelectPlaceholderOnly(storeSel);
      this.#resetSelectPlaceholderOnly(rangeSel);
      if (!regionKey || !retailVal || !regions[regionKey]) return;
      const retailerNode = regions[regionKey].find(
        (n) => n && (n.value === retailVal || n.text === retailVal),
      );
      if (!retailerNode || !Array.isArray(retailerNode.choices)) return;
      this.#fillSelectFromNodes(storeSel, retailerNode.choices);
    };

    const onStoreChange = () => {
      const regionKey = regionSel.value;
      const retailVal = retailSel.value;
      const storeVal = storeSel.value;
      this.#resetSelectPlaceholderOnly(rangeSel);
      if (!regionKey || !retailVal || !storeVal || !regions[regionKey]) return;
      const retailerNode = regions[regionKey].find(
        (n) => n && (n.value === retailVal || n.text === retailVal),
      );
      if (!retailerNode || !Array.isArray(retailerNode.choices)) return;
      const storeNode = retailerNode.choices.find(
        (n) => n && (n.value === storeVal || n.text === storeVal),
      );
      if (
        !storeNode ||
        !Array.isArray(storeNode.choices) ||
        storeNode.choices.length === 0
      ) {
        return;
      }
      this.#fillSelectFromNodes(rangeSel, storeNode.choices);
    };

    regionSel.addEventListener("change", onRegionChange, { signal });
    retailSel.addEventListener("change", onRetailChange, { signal });
    storeSel.addEventListener("change", onStoreChange, { signal });
  }

  /** @param {HTMLSelectElement} regionSel @param {Record<string, unknown[]>} regions */
  #populateRegionOptions(regionSel, regions) {
    const placeholderText =
      regionSel.getAttribute("data-placeholder") ||
      regionSel.dataset.placeholder ||
      "Region";
    regionSel.replaceChildren();
    const ph = document.createElement("option");
    ph.value = "";
    ph.selected = true;
    ph.className = "gf_placeholder";
    ph.textContent = placeholderText;
    regionSel.appendChild(ph);
    for (const name of Object.keys(regions)) {
      const opt = document.createElement("option");
      opt.value = name;
      opt.textContent = name;
      regionSel.appendChild(opt);
    }
  }

  /** @param {HTMLSelectElement} select */
  #resetSelectPlaceholderOnly(select) {
    const placeholderLabel =
      select.getAttribute("data-placeholder") ||
      select.dataset.placeholder ||
      "—";
    select.replaceChildren();
    const ph = document.createElement("option");
    ph.value = "";
    ph.selected = true;
    ph.className = "gf_placeholder";
    ph.textContent = placeholderLabel;
    select.appendChild(ph);
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {Array<{ text?: string, value?: string }>} nodes
   */
  #fillSelectFromNodes(select, nodes) {
    const placeholderLabel =
      select.getAttribute("data-placeholder") ||
      select.dataset.placeholder ||
      "—";
    select.replaceChildren();
    const ph = document.createElement("option");
    ph.value = "";
    ph.selected = true;
    ph.className = "gf_placeholder";
    ph.textContent = placeholderLabel;
    select.appendChild(ph);
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!node) continue;
      const v = node.value ?? node.text;
      const t = node.text ?? node.value;
      if (v == null && t == null) continue;
      const opt = document.createElement("option");
      opt.value = String(v);
      opt.textContent = t != null ? String(t) : String(v);
      select.appendChild(opt);
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = undefined;
  }

  /** Hides the form shell and shows the thank-you panel (accessibility: focus + scroll). */
  #showRegistrationSuccess(message) {
    const shell = this.querySelector("[data-product-registration-form-shell]");
    const panel = this.querySelector("[data-product-registration-success]");
    const msgEl = panel?.querySelector(
      ".product-registration__success-message",
    );
    if (msgEl) {
      msgEl.textContent = message;
    }
    if (shell) {
      shell.hidden = true;
    }
    if (panel) {
      panel.hidden = false;
      panel.focus({ preventScroll: true });
      panel.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  #stampAkismetField() {
    const ak = this.querySelector("#ak_js_1");
    if (ak) {
      ak.setAttribute("value", String(Date.now()));
    }
  }

  /**
   * Fields that must be valid before the receipt file control is used (same as required * above upload).
   * @param {HTMLFormElement} form
   * @returns {{ ok: boolean, focusEl: HTMLElement | null }}
   */
  #getReceiptPrerequisiteStatus(form) {
    const first = /** @type {HTMLInputElement | null} */ (
      form.querySelector("#input_23_1_3")
    );
    const last = /** @type {HTMLInputElement | null} */ (
      form.querySelector("#input_23_1_6")
    );
    const email = /** @type {HTMLInputElement | null} */ (
      form.querySelector("#input_23_2")
    );
    const region = form.querySelector('select[name="region"]');
    const retail = form.querySelector('select[name="retail"]');
    const store = form.querySelector('select[name="store"]');
    const range = form.querySelector('select[name="range"]');
    const buyingFor = /** @type {HTMLSelectElement | null} */ (
      form.querySelector("#input_23_25")
    );

    if (!first?.value?.trim()) return { ok: false, focusEl: first };
    if (!last?.value?.trim()) return { ok: false, focusEl: last };
    if (!email?.value?.trim()) return { ok: false, focusEl: email };
    if (typeof email.checkValidity === "function" && !email.checkValidity()) {
      return { ok: false, focusEl: email };
    }
    if (!region?.value) return { ok: false, focusEl: region };
    if (!retail?.value) return { ok: false, focusEl: retail };
    if (!store?.value) return { ok: false, focusEl: store };
    if (!range?.value) return { ok: false, focusEl: range };
    if (!buyingFor?.value) return { ok: false, focusEl: buyingFor };

    return { ok: true, focusEl: null };
  }

  /**
   * Full mandatory checks before submit (form has `novalidate`; chained selects are not `required`).
   * @param {HTMLFormElement} form
   * @returns {{ ok: boolean, focusEl: HTMLElement | null, message: string }}
   */
  #getSubmitBlockingIssues(form) {
    const pre = this.#getReceiptPrerequisiteStatus(form);
    if (!pre.ok) {
      return {
        ok: false,
        focusEl: pre.focusEl,
        message: "Please fill in all required fields marked with *.",
      };
    }

    const fileInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[type="file"][name="receipt"]')
    );
    const file = fileInput?.files?.[0];
    if (!file || file.size === 0) {
      return {
        ok: false,
        focusEl: fileInput,
        message:
          "Please upload a copy of your retail store receipt or invoice.",
      };
    }
    if (file.size > MAX_RECEIPT_BYTES) {
      return {
        ok: false,
        focusEl: fileInput,
        message: "Receipt file is too large (max 20 MB).",
      };
    }

    const serial = /** @type {HTMLInputElement | null} */ (
      form.querySelector("#input_23_10")
    );
    if (!serial?.value?.trim()) {
      return {
        ok: false,
        focusEl: serial,
        message: "Please enter your serial code or receipt / invoice number.",
      };
    }

    return { ok: true, focusEl: null, message: "" };
  }

  /**
   * @param {HTMLFormElement} form
   * @param {AbortSignal} signal
   */
  #bindReceiptValidation(form, signal) {
    const input = form.querySelector('input[type="file"][name="receipt"]');
    const liveMsg = form.querySelector("#live_validation_message_23_4");
    if (!input) return;

    const showPrerequisiteMessage = () => {
      if (liveMsg) {
        liveMsg.textContent = RECEIPT_PREREQUISITE_MESSAGE;
        liveMsg.classList.remove("validation_message--hidden-on-empty");
      }
    };

    const clearPrerequisiteMessageIfShown = () => {
      if (liveMsg && liveMsg.textContent === RECEIPT_PREREQUISITE_MESSAGE) {
        liveMsg.textContent = "";
      }
    };

    input.addEventListener(
      "click",
      (e) => {
        const { ok, focusEl } = this.#getReceiptPrerequisiteStatus(form);
        if (ok) {
          clearPrerequisiteMessageIfShown();
          return;
        }
        e.preventDefault();
        showPrerequisiteMessage();
        focusEl?.focus?.();
      },
      { signal, capture: true },
    );

    input.addEventListener(
      "change",
      () => {
        const { ok } = this.#getReceiptPrerequisiteStatus(form);
        if (!ok) {
          input.value = "";
          showPrerequisiteMessage();
          return;
        }

        const file = input.files && input.files[0];
        if (!file) {
          if (liveMsg) liveMsg.textContent = "";
          return;
        }
        if (file.size > MAX_RECEIPT_BYTES) {
          input.value = "";
          if (liveMsg) {
            liveMsg.textContent = "File is too large (max 20 MB).";
            liveMsg.classList.remove("validation_message--hidden-on-empty");
          }
        } else if (liveMsg) {
          liveMsg.textContent = "";
        }
      },
      { signal },
    );

    const refreshGateHint = () => {
      if (!liveMsg || liveMsg.textContent !== RECEIPT_PREREQUISITE_MESSAGE) {
        return;
      }
      if (this.#getReceiptPrerequisiteStatus(form).ok) {
        liveMsg.textContent = "";
      }
    };

    for (const sel of [
      "#input_23_1_3",
      "#input_23_1_6",
      "#input_23_2",
      "#input_23_25",
      'select[name="region"]',
      'select[name="retail"]',
      'select[name="store"]',
      'select[name="range"]',
    ]) {
      const el = form.querySelector(sel);
      el?.addEventListener("input", refreshGateHint, { signal });
      el?.addEventListener("change", refreshGateHint, { signal });
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {AbortSignal} signal
   */
  #bindSubmit(form, signal) {
    const submitButton = this.querySelector("#gform_submit_button_23");
    const statusNode = this.querySelector("#gform_status_23");
    const endpoint = form.getAttribute("action");
    const stageEndpoint = form.dataset.stageEndpoint || "";
    const token = form.dataset.datajetToken || "";
    const stageToken = form.dataset.stageToken || "";

    const setStatus = (message, isError) => {
      if (!statusNode) return;
      statusNode.textContent = message;
      statusNode.classList.toggle("gform_status--error", !!isError);
      statusNode.classList.toggle("gform_status--success", !isError);
    };

    const buildPayload = (formData) => {
      const payload = {
        submitted_at: new Date().toISOString(),
        source_url: window.location.href,
      };

      formData.forEach((value, key) => {
        if (value instanceof File) {
          return;
        }

        if (Object.prototype.hasOwnProperty.call(payload, key)) {
          if (!Array.isArray(payload[key])) {
            payload[key] = [payload[key]];
          }
          payload[key].push(value);
        } else {
          payload[key] = value;
        }
      });

      return payload;
    };

    form.addEventListener(
      "submit",
      async (event) => {
        event.preventDefault();

        const issues = this.#getSubmitBlockingIssues(form);
        if (!issues.ok) {
          setStatus(issues.message, true);
          issues.focusEl?.focus?.();
          return;
        }

        if (
          typeof form.reportValidity === "function" &&
          !form.reportValidity()
        ) {
          return;
        }

        if (submitButton) submitButton.disabled = true;
        setStatus("Submitting...", false);

        try {
          if (!endpoint) {
            throw new Error("Form action is missing.");
          }
          if (!stageEndpoint) {
            throw new Error(
              "Stage endpoint (data-stage-endpoint) is not configured.",
            );
          }

          const formData = new FormData(form);
          const receiptFile = formData.get("receipt");
          const payload = buildPayload(formData);

          if (
            !receiptFile ||
            !(receiptFile instanceof File) ||
            receiptFile.size === 0
          ) {
            throw new Error(
              "Please upload a copy of your retail store receipt or invoice.",
            );
          }
          if (receiptFile.size > MAX_RECEIPT_BYTES) {
            throw new Error("Receipt file is too large (max 20 MB).");
          }

          /** @type {Record<string, string>} */
          const djHeaders = {};
          if (token) {
            djHeaders["X-DataJet-Token"] = token;
            djHeaders.token = token;
          }

          /** @type {Record<string, string>} */
          const stageHeaders = { "Content-Type": "application/json" };
          if (stageToken) {
            stageHeaders["X-DataJet-Token"] = stageToken;
            stageHeaders.token = stageToken;
          }

          // Phase 1: Get staged upload URL from DataJet
          setStatus("Preparing upload...", false);
          const stageResponse = await fetch(stageEndpoint, {
            method: "POST",
            headers: stageHeaders,
            body: JSON.stringify({
              token: stageToken,
              properties: {
                payload: {
                  action: "prepare_upload",
                  r_filename: receiptFile.name,
                  r_mime_type: receiptFile.type || "application/octet-stream",
                },
              },
            }),
          });
          if (!stageResponse.ok) {
            const errorText = await stageResponse.text().catch(() => "");
            throw new Error(
              "Stage request failed: " +
                stageResponse.status +
                (errorText ? " " + errorText : ""),
            );
          }
          const stageResult = await stageResponse.json().catch(() => null);
          const stageValue =
            stageResult?.body?.return_value ?? stageResult?.return_value;
          if (!stageValue || stageValue.status !== "STAGE_READY") {
            throw new Error(
              stageValue?.message || "Failed to prepare file upload.",
            );
          }
          const stagedTarget = stageValue.staged_targets?.[0];
          if (!stagedTarget?.url || !stagedTarget?.resourceUrl) {
            throw new Error(
              "Staged upload target is missing url or resourceUrl.",
            );
          }

          // Upload file directly to Shopify's staged URL
          setStatus("Uploading receipt...", false);
          const uploadForm = new FormData();
          for (const param of stagedTarget.parameters || []) {
            uploadForm.append(param.name, param.value);
          }
          uploadForm.append("file", receiptFile, receiptFile.name);
          const uploadResponse = await fetch(stagedTarget.url, {
            method: "POST",
            body: uploadForm,
          });
          if (!uploadResponse.ok) {
            throw new Error(
              "Receipt upload failed (status " + uploadResponse.status + ").",
            );
          }

          // Phase 2: Submit registration with staged resource URL
          setStatus("Submitting...", false);
          payload.staged_resource_url = stagedTarget.resourceUrl;
          payload.r_mime_type = receiptFile.type || "application/octet-stream";

          const response = await fetch(endpoint, {
            method: "POST",
            headers: { ...djHeaders, "Content-Type": "application/json" },
            body: JSON.stringify({ token, properties: { payload } }),
          });

          if (!response.ok) {
            const errorText = await response.text().catch(() => "");
            throw new Error(
              "Request failed with status " +
                response.status +
                (errorText ? ": " + errorText : ""),
            );
          }

          const result = await response.json().catch(() => null);
          const returnValue = result?.body?.return_value;
          if (returnValue?.status === "ERROR") {
            throw new Error(
              returnValue.message || "DataJet returned an error.",
            );
          }

          this.#showRegistrationSuccess(
            this.dataset.successMessage?.trim() || REGISTRATION_SUCCESS_MESSAGE,
          );
        } catch (error) {
          setStatus(
            error && typeof error.message === "string"
              ? error.message
              : "Unable to submit right now.",
            true,
          );
        } finally {
          if (submitButton) submitButton.disabled = false;
        }
      },
      { signal },
    );
  }
}

if (!customElements.get("product-registration")) {
  customElements.define("product-registration", ProductRegistration);
}
